import { useState } from 'react'
import {
    Alert,
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    MenuItem,
    TextField,
} from '@mui/material'

import { useUpdateDestinationMutation } from '@/apis/v2Api'
import {
    adDestinationPatch,
    adEditForm,
    defaultEventMap,
    formatEventMap,
    v2ErrorMessage,
    type AdDestinationKind,
    type AdEditForm,
    type Destination,
} from '@/apis/v2Mappers'

/** Edit a Meta CAPI / GA4 MP destination: ids, rotate the credential, test event code, event map. */
export function EditAdDestinationDialog({
    projectId,
    destination,
    realtimeAllowed,
    onClose,
}: {
    projectId: string
    destination: Destination & { type: AdDestinationKind }
    realtimeAllowed: boolean
    onClose: () => void
}) {
    const [form, setForm] = useState<AdEditForm>(() => adEditForm(destination))
    const [update, state] = useUpdateDestinationMutation()
    const [error, setError] = useState<string | null>(null)
    const meta = destination.type === 'meta_capi'
    const set = (k: keyof AdEditForm) => (e: { target: { value: string } }) =>
        setForm({ ...form, [k]: e.target.value })

    const save = async () => {
        const patch = adDestinationPatch(destination, form)
        if (!patch.ok) {
            setError(patch.error)
            return
        }
        if (Object.keys(patch.body).length === 0) {
            onClose()
            return
        }
        try {
            await update({ projectId, id: destination.id, ...patch.body }).unwrap()
            onClose()
        } catch (err) {
            setError(v2ErrorMessage(err) ?? 'Saving failed')
        }
    }

    return (
        <Dialog open onClose={onClose} maxWidth="sm" fullWidth>
            <DialogTitle>
                Edit {meta ? 'Meta Conversions API' : 'GA4 Measurement Protocol'} destination
            </DialogTitle>
            <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                {error && <Alert severity="error">{error}</Alert>}
                <TextField
                    size="small"
                    label="Name"
                    value={form.name}
                    onChange={set('name')}
                    sx={{ mt: 1 }}
                />
                {meta ? (
                    <TextField
                        size="small"
                        label="Pixel / dataset id"
                        required
                        value={form.pixelId}
                        onChange={set('pixelId')}
                    />
                ) : (
                    <TextField
                        size="small"
                        label="Measurement id"
                        required
                        value={form.measurementId}
                        onChange={set('measurementId')}
                    />
                )}
                <TextField
                    size="small"
                    label={meta ? 'New access token' : 'New API secret'}
                    type="password"
                    autoComplete="off"
                    helperText={`Leave empty to keep the current one (${destination.credential?.hint ?? '••••'}). A new one replaces it immediately.`}
                    value={form.credential}
                    onChange={set('credential')}
                />
                {meta && (
                    <TextField
                        size="small"
                        label="Test event code"
                        helperText="From Events Manager → Test events. Clear it when you're done testing."
                        value={form.testEventCode}
                        onChange={set('testEventCode')}
                    />
                )}
                <TextField
                    size="small"
                    label="Event map (additions and renames)"
                    multiline
                    minRows={3}
                    placeholder={meta ? 'quote_sent = Lead' : 'quote_sent = generate_lead'}
                    helperText={`One "yom_event = ${meta ? 'MetaEvent' : 'ga4_event'}" per line. Defaults, sent unless renamed here: ${formatEventMap(
                        { ...defaultEventMap(destination.type) },
                    ).replace(/\n/g, ', ')}.`}
                    value={form.eventMap}
                    onChange={set('eventMap')}
                    slotProps={{ htmlInput: { style: { fontFamily: 'monospace' } } }}
                />
                <TextField
                    size="small"
                    select
                    label="Mode"
                    value={form.mode}
                    onChange={e => setForm({ ...form, mode: e.target.value as AdEditForm['mode'] })}
                >
                    <MenuItem value="batched">Batched</MenuItem>
                    <MenuItem
                        value="realtime"
                        disabled={!realtimeAllowed && destination.mode !== 'realtime'}
                    >
                        Realtime{realtimeAllowed ? '' : ' (paid plans)'}
                    </MenuItem>
                </TextField>
                <TextField
                    size="small"
                    label="Only this sub-tenant (optional)"
                    value={form.subTenant}
                    onChange={set('subTenant')}
                />
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose}>Cancel</Button>
                <Button
                    variant="contained"
                    disabled={state.isLoading || !(meta ? form.pixelId : form.measurementId).trim()}
                    onClick={() => {
                        void save()
                    }}
                >
                    Save
                </Button>
            </DialogActions>
        </Dialog>
    )
}
